import { Component, EventEmitter, Input, Output } from '@angular/core';
import { initials, PanelMember, PresenceSplit, splitByPresence } from '../community-format';
import { activeThreads, threadPreview, ThreadSummary } from '../community-threads';

/**
 * Right panel with Details / Threads tabs. Details shows the same data as the
 * classic Details view. Threads lists every thread in the space (shown only when
 * there is at least one); the open thread's content is projected by the parent so
 * the inherited thread handlers stay in one place.
 */
@Component({
  selector: 'app-cm-side-panel',
  templateUrl: './cm-side-panel.component.html',
  styleUrls: ['./cm-side-panel.component.scss'],
})
export class CmSidePanelComponent {
  @Input() channel: any;
  @Input() members: PanelMember[] = [];
  @Input() memberTotal = 0;
  @Input() hasThread = false;
  /** The space's messages; threads are the ones with replies. */
  @Input() messages: any[] = [];
  @Input() search = '';
  /** userIds currently online; null when presence is off or not known yet. */
  @Input() onlineIds: Set<string> | null = null;

  /** Offline members shown before "+ N more members" expands the list. */
  readonly OFFLINE_PREVIEW = 5;
  showAllOffline = false;

  tab: 'details' | 'threads' = 'details';
  private threadCache: { key: string; list: ThreadSummary[] } | null = null;

  @Output() closeThread = new EventEmitter<void>();
  @Output() openThread = new EventEmitter<any>();
  @Output() close = new EventEmitter<void>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() searchSubmit = new EventEmitter<void>();

  get split(): PresenceSplit {
    return splitByPresence(this.members, this.onlineIds);
  }

  get threads(): ThreadSummary[] {
    const key = (this.messages || []).map(m => m?.threadReplies?.length || 0).join(',');
    if (this.threadCache?.key !== key) this.threadCache = { key, list: activeThreads(this.messages) };
    return this.threadCache.list;
  }

  get showThreadsTab(): boolean {
    return this.hasThread || this.threads.length > 0;
  }

  get pane(): 'details' | 'list' | 'thread' {
    if (this.hasThread) return 'thread';
    return this.tab === 'threads' && this.threads.length ? 'list' : 'details';
  }

  selectTab(tab: 'details' | 'threads'): void {
    this.tab = tab;
    if (this.hasThread) this.closeThread.emit();
  }

  preview(message: any): string {
    return threadPreview(message);
  }

  starterInitials(message: any): string {
    return message?._sender?.shortName || initials(message?._sender?.nickname);
  }

  trackUser(_: number, u: PanelMember): string {
    return u?.userId;
  }

  trackThread(_: number, t: ThreadSummary): string {
    return t.message?.messageId;
  }
}
